import { ContainerType, getReactionCountColorBasedOnStatus, getReactionWrapperColorBasedOnStatus, newContainer } from "./utils";
import { Group } from "konva/lib/Core";
import { TopPadding } from ".";
import { Rect } from "konva/lib/shapes/Rect";
import { Text } from "konva/lib/shapes/Text";
import { IWhiteboardRendererContext } from "../../whiteboard-renderer";
import { IReactedUser } from "@gtmhub/whiteboards/components/tooltip.component";
import { IDraftGoalState, INoteState, WhiteboardShape } from "../../shapes";
import unknownurl from "../../../../../../../wwwroot/fe/img/avatars/unknown.svg";

export enum ReactionWrapperSize {
  WIDTH = 41,
  HEIGHT = 24,
}

export enum ReactionStatus {
  DEFAULT = "default",
  ACTIVE = "active",
  DISABLED = "disabled",
}

export type ReactionWrapperColor = {
  fill: string;
  stroke: string;
};

export enum ReactionWrapperPositionAdd {
  X = 5,
  Y = 5,
}

export const REACTION_WRAPPER_GAP = 6;
export const TOOLTIP_OFFSET_Y = 12;
export const TOOLTIP_DELAY = 500;
export const DIGIT_WIDTH = 7;

export enum ClicksCount {
  FONT_SIZE = 14,
  FONT_STYLE = "600",
  FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
  ENABLED_COLOR = "#283D5C",
  DISABLED_COLOR = "#FFFFFF",
  OFFSET_X = 26,
  OFFSET_Y = 6,
}

export class Reactions {
  private reactionsContainer: Group;
  private reactedWith = [];
  private tooltipDelayTimeout: ReturnType<typeof setTimeout>;

  constructor(private shape: WhiteboardShape<INoteState | IDraftGoalState>, private context: IWhiteboardRendererContext) {}

  build(params: { emoji: Text; numberOfReactions: number; reactionsCount: number; reactionStatus: ReactionStatus }): Group {
    this.reactionsContainer = newContainer({ id: "reactions-group", y: TopPadding.HEIGHT, name: "voting" }, ContainerType.Group) as Group;

    this.reactedWith.push(params.emoji.clone());

    for (const reaction of this.reactedWith) {
      this.reactionsContainer.add(
        this.buildReaction({
          reaction,
          numberOfReactions: params.numberOfReactions,
          reactionsCount: params.reactionsCount,
          reactionStatus: params.reactionStatus,
        })
      );
    }

    this.shape.getStage().batchDraw();

    return this.reactionsContainer;
  }

  private buildItems(reactionName: string): IReactedUser[] {
    if (!this.shape.state.reactions) return [];

    const users = [];
    const reaction = this.shape.state.reactions?.find((r) => r.name === reactionName);

    if (reaction) {
      reaction.userIds.forEach((id) => {
        const user = {
          name: "",
          avatar: unknownurl,
          active: false,
        };

        const assignee = this.context.assignees(id);
        if (assignee) {
          user.name = assignee.name;
          user.avatar = assignee.picture || unknownurl;
          user.active = assignee.isActive;
        }

        users.push(user);
      });
    }

    return users;
  }

  private buildReaction(params: { reaction: Text; numberOfReactions: number; reactionsCount: number; reactionStatus: ReactionStatus }): Group {
    const singleReactionGroup = newContainer({ id: "single-reaction-group", name: "voting-reaction voting" }, ContainerType.Group) as Group;

    const reactionWrapperHolder = newContainer(
      {
        x: params.reactionsCount * ReactionWrapperSize.WIDTH,
        width: ReactionWrapperSize.WIDTH + REACTION_WRAPPER_GAP,
        fill: "",
        cornerRadius: 0,
        name: "voting",
      },
      ContainerType.Wrapper
    ) as Rect;

    const reactionWrapper = newContainer({
      name: "reaction-wrapper voting",
      width: ReactionWrapperSize.WIDTH + this.expand(params.numberOfReactions),
      height: ReactionWrapperSize.HEIGHT,
      x: params.reactionsCount * (ReactionWrapperSize.WIDTH + REACTION_WRAPPER_GAP),
      ...getReactionWrapperColorBasedOnStatus(params.reactionStatus),
      strokeWidth: 1,
    });

    singleReactionGroup.on("mouseenter", () => {
      this.tooltipDelayTimeout = setTimeout(() => {
        const users = this.buildItems(params.reaction.id());
        dispatchEvent(new CustomEvent("show-tooltip", { detail: { users, shape: reactionWrapper } }));
      }, TOOLTIP_DELAY);

      singleReactionGroup.setAttrs({ timeoutId: this.tooltipDelayTimeout });

      const wrapper = singleReactionGroup.findOne(".reaction-wrapper");
      wrapper.setAttrs(getReactionWrapperColorBasedOnStatus(params.reactionStatus, true));
      this.shape.getStage().batchDraw();
    });

    singleReactionGroup.on("mouseleave", () => {
      clearTimeout(this.tooltipDelayTimeout);

      const tooltipEl = document.getElementById("tooltip");

      const tooltipLeft = tooltipEl.offsetLeft;
      const tooltipTop = tooltipEl.offsetTop;
      const tooltipWidth = tooltipEl.clientWidth;
      const tooltipHeight = tooltipEl.clientHeight;

      const pointerPos = this.shape.getStage().getPointerPosition();

      const pointerX = pointerPos.x;
      const pointerY = pointerPos.y;

      const isInsideTheTooltip = pointerX >= tooltipLeft && pointerX <= tooltipLeft + tooltipWidth && pointerY >= tooltipTop && pointerY <= tooltipTop + tooltipHeight;

      if (!isInsideTheTooltip) {
        tooltipEl.style.visibility = "hidden";
      }

      const wrapper = singleReactionGroup.findOne(".reaction-wrapper");
      wrapper.setAttrs(getReactionWrapperColorBasedOnStatus(params.reactionStatus));
      this.shape.getStage().batchDraw();
    });

    singleReactionGroup.on("click", (e) => singleReactionGroup.fire("reaction-taken", { e, emoji: params.reaction }, true));

    const clicksCount = new Text({
      x: (reactionWrapper.x() + ClicksCount.OFFSET_X) as number,
      y: ClicksCount.OFFSET_Y as number,
      name: "clicks-count voting",
      text: params.numberOfReactions.toString(),
      fontSize: ClicksCount.FONT_SIZE as number,
      fontStyle: ClicksCount.FONT_STYLE as string,
      fontFamily: ClicksCount.FONT as string,
      fill: getReactionCountColorBasedOnStatus(params.reactionStatus),
    });

    params.reaction.x(reactionWrapper.x() + ReactionWrapperPositionAdd.X);
    params.reaction.y(ReactionWrapperPositionAdd.Y);

    singleReactionGroup.add(reactionWrapperHolder, reactionWrapper, params.reaction, clicksCount);

    return singleReactionGroup;
  }

  private expand(numberOfReactions: number): number {
    const numberLength = numberOfReactions.toString().length;
    return (numberLength - 1) * DIGIT_WIDTH;
  }
}
